import {
  useEffect, cloneElement, ReactElement,
} from 'react';
// import PropTypes from 'prop-types';
import { connect } from 'react-redux';
// import hoistNonReactStatic from 'hoist-non-react-statics';
import isEmpty from 'lodash/isEmpty';

import LoginHomePage from '../../auth/LoginHomePage';
import * as actions from '../store/actions';
import Page403 from '../../../components/errors/403';
import { PROJECT_AVTOAPP } from '../../core/constants/projects';
import { UserProfile } from '../../auth/store/userAPI';
import { PermissionInterface } from '../../permissions/api/permissionsAPI';

type PermissionType = string | string[];

export const checkUserPermissions = (user, permissions, operationKey: string | string[], permissionType: PermissionType = 'available') => {
  // Если operationKey не передан, проверяем только авторизацию
  if (!operationKey || (Array.isArray(operationKey) && operationKey.length === 0)) {
    return !!user?.token;
  }
  
  // Если operationKey - массив, ищем разрешения для любой из операций
  const targetOperationKeys = Array.isArray(operationKey) ? operationKey : [operationKey];
  
  // Ищем разрешения для всех указанных operationKey
  const foundPermissions = Array.isArray(permissions)
    ? permissions?.filter((item) => targetOperationKeys.includes(item.operationKey))
    : [];
  
  // Если не найдено ни одного разрешения для указанных операций
  if (foundPermissions.length === 0) {
    return false;
  }
  
  const permissionTypes = (typeof permissionType === 'string')
    ? [permissionType]
    : permissionType;

  // Проверяем, есть ли хотя бы одно разрешение с нужным типом для любой из операций
  const hasPermission = foundPermissions.some((permission) => {
    return permissionTypes.some((type) => permission && permission[type]);
  });

  return user?.token && hasPermission;
};

/**
 * Проверка на то, что пользователь авторизован в системе и обладает указанными разрешениями.
 * Если пользователь не авторизован, то вернет страницу авторизации.
 * Если пользователь не обладает указанными разрешениями, то вернет 403ю страницу.
 *
 * @param WrappedComponent
 * @param {String|String[]} operationKey Тип операции или массив типов операций, для которых проверяются разрешения.
 *                              Если не указано, то просто проверяет авторизацию пользователя, без проверки разрешений
 * @param {String|String[]} permissionType Тип разрешения. Может быть строкой или массивом строк.
 *                                      Если не указан, то проверяется общее разрешение 'available'
 * @returns {componentToPassDown|LoginHomePage|Page403}
 */

interface UserHasPermissionV2Props {
  componentToPassDown: ReactElement,
  operationKey: string | string[],
  permissionType: string | string[],
  user: UserProfile,
  permissions: PermissionInterface[],
  loading: boolean,
  getUserPermissions: Function,
}

export const UserHasPermissionV2 = (props: UserHasPermissionV2Props = {
  permissionType: 'available',
  componentToPassDown: undefined,
  operationKey: '',
  user: undefined,
  permissions: undefined,
  loading: false,
  getUserPermissions: undefined,
}): ReactElement => {
  const {
    componentToPassDown,
    user,
    permissions,
    getUserPermissions,
    loading,
    operationKey,
    permissionType,
  } = props;

  const customerId = user?.customerId;

  useEffect(() => {
    if (customerId && !loading && isEmpty(permissions)) {
      getUserPermissions({ customerId, projectId: PROJECT_AVTOAPP });
    }
  }, [customerId, loading, permissions, getUserPermissions]);

  // hoistNonReactStatic(Enhance, WrappedComponent);

  if (!user?.token || user?.error) {
    return (<LoginHomePage />);
  }

  if (checkUserPermissions(user, permissions, operationKey, permissionType)) {
    return (
      <div>
        { cloneElement(componentToPassDown) }
      </div>
    );
  }

  if (isEmpty(permissions)) {
    return null;
  }

  return (<Page403 />);
};

const mapStateToProps = (state) => {
  return {
    user: state.auth?.profile,
    permissions: state.permissions?.permissions,
    loading: state.permissions?.loading,
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    getUserPermissions: (payload) => dispatch(actions.getUserPermissions(payload)),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(UserHasPermissionV2);